import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

function StatCounter({ target, suffix = "", duration = 1200 }) {
  const [value, setValue] = useState(0);
  const elRef = useRef(null);
  const animatedRef = useRef(false);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !animatedRef.current) {
            animatedRef.current = true;
            let start = null;
            function step(ts) {
              if (!start) start = ts;
              const progress = Math.min((ts - start) / duration, 1);
              const val = target * progress;
              setValue(
                target % 1 === 0 ? Math.floor(val) : Number(val.toFixed(1)),
              );
              if (progress < 1) requestAnimationFrame(step);
            }
            requestAnimationFrame(step);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.4 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return (
    <strong ref={elRef}>
      {value}
      {suffix}
    </strong>
  );
}

export default function AllCourses() {
  window.scrollTo({ top: 0, behavior: "smooth" });
  return (
    <>
      <>
        <div className="page-head">
          <div className="container">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/">Home</Link>
              </li>
              <li className="breadcrumb-item active">Courses</li>
            </ol>
            <h1>Browse Our Course Catalog</h1>
            <p className="text-mono" style={{ color: "#B9C0F0" }}>
              8,559+ students trained · 5/5 rating · 10+ years experience
            </p>
          </div>
        </div>
        <section className="section">
          <div className="container">
            <div className="section-head">
              <span className="kicker">Beginner tracks</span>
              <h2>Perfect for those starting their learning journey</h2>
            </div>
            <div className="row">
              <div className="col-md-6 mb-4">
                <div className="course-card">
                  <div className="cc-media" />
                  <div className="cc-body">
                    <span className="level-pill beginner">Most Popular</span>
                    <h3>Master Course</h3>
                    <p className="cc-tag">
                      Complete Digital Marketing Foundation
                    </p>
                    <p className="text-muted-soft small">
                      Digital marketing basics, SEO &amp; content strategy, real
                      campaign case studies and analytics-driven decision
                      making.
                    </p>
                    <ul>
                      <li>Digital Marketing Fundamentals</li>
                      <li>SEO &amp; Content Strategy</li>
                      <li>Real Campaign Case Studies &amp; Practice</li>
                      <li>Analytics &amp; ROI Tracking</li>
                      <li>Lifetime Support Community</li>
                    </ul>
                    <div className="cc-foot">
                      <span className="cc-duration">3 Months</span>
                      <Link
                        to="/master_courses"
                        className="btn btn-outline-ink btn-sm"
                      >
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-6 mb-4">
                <div className="course-card">
                  <div className="cc-media" />
                  <div className="cc-body">
                    <span className="level-pill beginner">Beginner</span>
                    <h3>Weekend Crash Course</h3>
                    <p className="cc-tag">
                      Social Media Marketing for Beginners
                    </p>
                    <p className="text-muted-soft small">
                      Master social media marketing from scratch — platforms,
                      content creation and audience growth.
                    </p>
                    <ul>
                      <li>Platform Algorithms &amp; Best Practices</li>
                      <li>Marketing Terminologies &amp; Trends</li>
                      <li>Organic &amp; Paid Growth Strategies</li>
                      <li>Practical Support &amp; Q&amp;A</li>
                      <li>Campaign Performance Analysis</li>
                    </ul>
                    <div className="cc-foot">
                      <span className="cc-duration">2 Months</span>
                      <Link
                        to="/weekend_courses"
                        className="btn btn-outline-ink btn-sm"
                      >
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="section bg-paper">
          <div className="container">
            <div className="section-head mx-auto text-center">
              <span className="kicker justify-content-center">Reviews</span>
              <h2>What our students say</h2>
            </div>
            <div className="row">
              <div className="col-md-4 mb-4">
                <div className="testi-card">
                  <span className="quote-mark">"</span>
                  <p>
                    This course completely changed my marketing perspective. Now
                    I run campaigns like a pro, not a guessing amateur. Worth
                    every penny!
                  </p>
                  <div className="testi-name">Rahul Sharma</div>
                  <div className="testi-role"></div>
                </div>
              </div>
              <div className="col-md-4 mb-4">
                <div className="testi-card">
                  <span className="quote-mark">"</span>
                  <p>
                    Excellent mentorship and support. The instructors are
                    experienced and explain complex concepts in simple terms.
                    5-star experience!
                  </p>
                  <div className="testi-name">Priya Verma</div>
                  <div className="testi-role"></div>
                </div>
              </div>
              <div className="col-md-4 mb-4">
                <div className="testi-card">
                  <span className="quote-mark">"</span>
                  <p>
                    Structured, practical, and honest about risk. Exactly what I
                    needed after years of guessing on my own.
                  </p>
                  <div className="testi-name">Amit Kumar</div>
                  <div className="testi-role">Haridwar</div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="section">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-7">
                <span className="kicker">Not sure which course to choose?</span>
                <h2 className="mt-2">
                  Get a free counselling call, no strings attached
                </h2>
                <p className="text-muted-soft">
                  We'll help you find the right course for your goals and
                  experience level. Available in , , Solan, Haridwar and online,
                  pan-India.
                </p>
              </div>
              <div className="col-lg-5 text-lg-right mt-3 mt-lg-0">
                <a
                  href="https://wa.me/7004654910?text=Hi%2C%20I%20saw%20your%20website%2C%20I%27m%20interested%20in%20your%20courses.%20Could%20you%20share%20more%20details%3F"
                  className="btn btn-wa btn-lg"
                  target="_blank"
                  rel="noopener"
                >
                  Get Free Counselling on WhatsApp
                </a>
              </div>
            </div>
            <hr className="section-divider my-5" />
            <div className="row text-center">
              <div className="col-6 col-md-2 mb-4">
                <div className="stat-block">
                  <StatCounter target={10} suffix="k+" />
                  <span>Teaching Hours</span>
                </div>
              </div>
              <div className="col-6 col-md-2 mb-4">
                <div className="stat-block">
                  <StatCounter target={5} />
                  <span>/5 Rating</span>
                </div>
              </div>
              <div className="col-6 col-md-2 mb-4">
                <div className="stat-block">
                  <strong>Google</strong>
                  <span>Certified Faculty</span>
                </div>
              </div>
              <div className="col-6 col-md-2 mb-4">
                <div className="stat-block">
                  <StatCounter target={95} suffix="%" />
                  <span>Completion Rate</span>
                </div>
              </div>
              <div className="col-6 col-md-2 mb-4">
                <div className="stat-block">
                  <strong>Pan</strong>
                  <span>India + Online</span>
                </div>
              </div>
              <div className="col-6 col-md-2 mb-4">
                <div className="stat-block">
                  <strong>5</strong>
                  <span>Branches Nationwide</span>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="section bg-paper">
          <div className="container">
            <div className="cta-band text-center">
              <h2>Ready to start your marketing journey?</h2>
              <p className="mb-4">
                Book your free, no-questions-asked demo class today.
              </p>
              <div
                className="d-flex justify-content-center flex-wrap"
                style={{ gap: "1rem" }}
              >
                <a
                  href="https://wa.me/7004654910?text=Hi%2C%20I%20saw%20your%20website%2C%20I%27m%20interested%20in%20your%20courses.%20Could%20you%20share%20more%20details%3F"
                  className="btn btn-brass btn-lg"
                  target="_blank"
                  rel="noopener"
                >
                  Book Free Demo
                </a>
                <a
                  href="tel:7004654910"
                  className="btn btn-outline-ink btn-lg"
                  style={{ borderColor: "rgba(255,255,255,.4)", color: "#fff" }}
                >
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </section>
      </>
    </>
  );
}
